import { memo } from "react";

import CheckCircle from "@zenith/assets/icons/CheckCircle";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import Info from "@zenith/assets/icons/Info";
import { PIN_LENGTH } from "@zenith/data/constants";

/**
 * Props for Pin Rules Component.
 * @interface
 * @name PinRulesComponentProps
 * @kind interface
 */
interface PinRulesComponentProps {
  /**
   * If `true`, the pin has repeated characters.
   * @default false
   * @property
   * @name isRepeated
   * @kind property
   * @memberof PinRulesComponentProps
   * @type {boolean}
   */
  isRepeated: boolean;
  /**
   * If `true`, the pin has sequential characters.
   * @default false
   * @property
   * @name isSequential
   * @kind property
   * @memberof PinRulesComponentProps
   * @type {boolean}
   */
  isSequential: boolean;
  /**
   * Necessary length for the pin.
   * @property
   * @name necessaryPinLength
   * @kind property
   * @memberof PinRulesComponentProps
   * @type {number}
   */
  necessaryPinLength: number;
  /**
   * Current pin length.
   * @property
   * @name pinLength
   * @kind property
   * @memberof PinRulesComponentProps
   * @type {number}
   */
  pinLength: number;
  /**
   * Standard HTML Attribute, for container classes.
   * @property
   * @name className
   * @kind property
   * @memberof PinRulesComponentProps
   * @type {string}
   */
  className: string;
}

/**
 * Memoized Pin Rules Component.
 * @constant
 * @name PinRulesComponent
 * @kind variable
 * @type {React.NamedExoticComponent<PinRulesComponentProps>}
 */
const PinRulesComponent: React.NamedExoticComponent<PinRulesComponentProps> =
  memo(
    /**
     * Pin Rules Component.
     * @function
     * @name PinRulesComponent
     * @kind function
     * @memberof PinRulesComponent
     * @param {PinRulesComponentProps} props Props for Pin Rules Component.
     * @returns {JSX.Element} Pin Rules Component.
     */
    function PinRulesComponent(props: PinRulesComponentProps): JSX.Element {
      const {
        className,
        isRepeated,
        isSequential,
        necessaryPinLength,
        pinLength,
      } = props;

      return (
        <div className={className}>
          <h3 className="mb-3 text-sm font-medium text-neutral-600">
            To protect your account, your Passcode must not have:
          </h3>

          <div className="mb-1 flex flex-row">
            {pinLength !== necessaryPinLength ? (
              <Info width={16} height={16} className="mr-2 text-neutral-600" />
            ) : isSequential ? (
              <CloseCircle className="mr-2" />
            ) : (
              <CheckCircle className="mr-2" />
            )}
            <p
              className={`text-xs ${
                pinLength !== necessaryPinLength
                  ? "text-neutral-600"
                  : isSequential
                  ? "text-primary-300"
                  : "text-accent-success"
              }`}
            >
              {necessaryPinLength === PIN_LENGTH
                ? "Sequential numbers only (example: 123456; 456789; 987654)"
                : "Sequential numbers only (example: 1234; 5678; 9876)"}
            </p>
          </div>
          <div className="flex flex-row">
            {pinLength !== necessaryPinLength ? (
              <Info width={16} height={16} className="mr-2 text-neutral-600" />
            ) : isRepeated ? (
              <CloseCircle className="mr-2" />
            ) : (
              <CheckCircle className="mr-2" />
            )}
            <p
              className={`text-xs ${
                pinLength !== necessaryPinLength
                  ? "text-neutral-600"
                  : isRepeated
                  ? "text-primary-300"
                  : "text-accent-success"
              }`}
            >
              {necessaryPinLength === PIN_LENGTH
                ? "Repeated numbers only (example: 111111; 444444; 000000)"
                : "Repeated numbers only (example: 1111; 4444; 0000)"}
            </p>
          </div>
        </div>
      );
    }
  );

export default PinRulesComponent;
